import { Pagination } from '@app/components/various/Pagination';
import { Table } from '@app/components/various/Table';
import { DEFAULT_ITEMS_COUNT_PER_REQUEST } from '@app/constants/limits';
import { getIsOrderEditable } from '@app/logic/Orders';
import { DELETE_SELECTIONS_MUTATION_KEY, useDeleteSelections } from '@app/services/hooks/orders/useDeleteSelections';
import { useSelectionsList } from '@app/services/hooks/orders/useSelectionsList';
import { getSkipFirstForListing } from '@app/utils/getSkipFirstForListing';
import { useAmplitude, useConfirmationGuard, useModalVisibility } from '@app/utils/hooks';
import { useSelectionsListTable } from '@app/utils/hooks/selections/useSelectionsListTable';
import { useModalToggle } from '@app/utils/hooks/useModalToggle';
import { useOrderStatusList } from '@app/utils/hooks/useOrderStatusList';
import { useSyncPageAfterDelete } from '@app/utils/hooks/useSyncPageAfterDelete';
import { isDefined } from '@app/utils/is';
import { isEmpty } from '@app/utils/isEmpty';
import { getOrderStatusColor } from '@app/utils/status';
import { useIsMutating } from '@tanstack/react-query';
import { Order } from '@typings';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { CopyToNewSelectionModal } from '../CopyToNewSelectionModal';
import { DownloadLinesheetModal } from '../DownloadLinesheetModal';
import { useOrdersSearchParams } from '../useOrdersSearchParams';

import { SelectionsListCard } from './SelectionsListCard';
import { SelectionsListCardSkeleton } from './SelectionsListCardSkeleton';
import { SelectionsListEmptyState } from './SelectionsListEmptyState';
import { SelectionsListTopBar } from './SelectionsListTopBar';

export const SelectionsList = () => {
  const { t } = useTranslation(['orders', 'common', 'selections', 'confirmationConfig']);
  const { showModal } = useModalVisibility('CreateOrderModal');
  const confirmationGuard = useConfirmationGuard();
  const { trackEvent } = useAmplitude();

  const [activeSelection, setActiveSelection] = React.useState<Nullable<Order.Essentials>>(null);
  const { deleteSelectionByIds } = useDeleteSelections();
  const {
    showModal: showCopyToMySelectionModal,
    isOpen: isCopyToMySelectionModalOpen,
    hideModal: hideCopyToMySelectionModal,
  } = useModalToggle();
  const {
    showModal: showLinesheetDownloadModal,
    isOpen: isLinesheetDownloadModalOpen,
    hideModal: hideLinesheetDownloadModal,
  } = useModalToggle();
  const { editable: editableStages } = useOrderStatusList();
  const { handlePageChange, handleSearchChange, handleSortChange, handleStatusChange, page, search, sortOrder, status } =
    useOrdersSearchParams();

  const statuses = React.useMemo(() => {
    return editableStages.filter(stage => !isEmpty(stage.value)).map(stage => stage.value);
  }, [editableStages]);

  const selectedStatuses = React.useMemo(() => (isEmpty(status) ? statuses : [status]), [status, statuses]) as Order.SelectionStatus[];

  const skipFirst = getSkipFirstForListing(page);
  const isDeletingAnySelection = useIsMutating({ mutationKey: [DELETE_SELECTIONS_MUTATION_KEY] }) > 0;

  const {
    data,
    isError,
    isLoading: isLoadingInitialQuery,
    isPlaceholderData,
  } = useSelectionsList({
    data: {
      limit: DEFAULT_ITEMS_COUNT_PER_REQUEST,
      search,
      skipFirst: getSkipFirstForListing(page),
      sortOrder,
      statuses: selectedStatuses,
    },
  });

  const isLoading = isLoadingInitialQuery || isPlaceholderData;

  const deliveryWindows = data?.deliveryWindows ?? [];
  const orders = data?.orders;
  const ordersCountTotal = data?.orderCount;

  useSyncPageAfterDelete({ ordersCountTotal, skipFirst });

  const handleCopyToNewSelection = React.useCallback(
    (order: Order.Essentials) => {
      setActiveSelection(order);
      showCopyToMySelectionModal();
    },
    [showCopyToMySelectionModal],
  );

  const handleLinesheetDownload = React.useCallback(
    (order: Order.Essentials) => {
      setActiveSelection(order);
      showLinesheetDownloadModal();
    },
    [showLinesheetDownloadModal],
  );

  const handleDelete = React.useCallback(
    (order: Order.Essentials) => {
      confirmationGuard(() => ({
        onOk: () => deleteSelectionByIds.mutate([order.order]),
        ...t('confirmationConfig:delete_order', { returnObjects: true }),
      }))();
    },
    [confirmationGuard, deleteSelectionByIds, t],
  );

  const { columnsVisibility, pagination, table } = useSelectionsListTable({
    deliveryWindows,
    handleCopyToNewSelection,
    handleDelete,
    handleLinesheetDownload,
    handlePageChange,
    handleSortChange,
    isLoading: isLoading || isDeletingAnySelection,
    orders,
    page,
    sortOrder,
  });

  const hasOrders = isDefined(orders) && !isEmpty(orders);
  const shouldShowEmptyState = (!isLoading && !hasOrders) || isError;
  const activeSelectionId = activeSelection?.order;
  const selectionNumber = activeSelection?.orderNumber;
  const buyer = activeSelection?.buyer;
  const downloadOnlySelected = isDefined(activeSelection) ? !getIsOrderEditable(activeSelection.status) : true;

  const handleSuccessDownloadLinesheetModal = () => {
    trackEvent({ name: 'downloadAction.button.linesheet.submit', properties: { path: 'selectionsList' } });
  };

  const handleSuccessCopyToNewSelection = () => {
    trackEvent({ name: 'selectionAction.button.copySelection.click', properties: { path: 'selectionsList' } });
  };

  return (
    <div className="flex flex-1 flex-col px-4 lg:px-8">
      <SelectionsListTopBar
        table={table}
        search={search}
        status={status}
        totalCount={ordersCountTotal}
        columnsVisibility={columnsVisibility}
        onSearchChange={handleSearchChange}
        onStatusChange={handleStatusChange}
      />
      {shouldShowEmptyState && <SelectionsListEmptyState isError={isError} />}
      {(isLoading || hasOrders) && (
        <div className="flex flex-1 flex-col gap-2">
          <Table
            dataTestId="selectionsList"
            isLoading={isLoading}
            table={table}
            getRowMarkerColor={row => getOrderStatusColor(row.original.status)}
            renderSkeletonCard={index => <SelectionsListCardSkeleton key={index} />}
            renderCard={(row, index) => (
              <SelectionsListCard
                key={index}
                row={row}
                deliveryWindows={deliveryWindows}
                onCopyToNewSelection={handleCopyToNewSelection}
                onLinesheetDownload={handleLinesheetDownload}
                onDelete={handleDelete}
              />
            )}
          />
          <Pagination
            onChange={table.setPageIndex}
            current={pagination.pageIndex}
            totalItems={ordersCountTotal}
            onActionButtonClick={showModal}
            actionButtonLabel={t('selections:create_selection')}
            isHidden={isLoadingInitialQuery}
          />
        </div>
      )}
      <DownloadLinesheetModal
        isOpen={isLinesheetDownloadModalOpen}
        onClose={hideLinesheetDownloadModal}
        onSuccess={handleSuccessDownloadLinesheetModal}
        location="order table"
        orderId={activeSelectionId}
        orderNumber={selectionNumber}
        downloadOnlySelected={downloadOnlySelected}
      />
      <CopyToNewSelectionModal
        isOpen={isCopyToMySelectionModalOpen}
        onClose={hideCopyToMySelectionModal}
        onSubmit={handleSuccessCopyToNewSelection}
        orderId={activeSelectionId}
        orderNumber={selectionNumber}
        defaultBuyer={buyer}
      />
    </div>
  );
};
